/**
 * PricingFields - the pricing form, for any entity that is priced.
 *
 * Pricing model + currency + budget, then the two fields the model implies
 * (fixed fee, contingency %). It was `MatterPricingFields`, unchanged apart
 * from the two things that were actually about matters: the copy under each
 * field, and the input ids. Both are props now, so a project renders the same
 * form with its own noun. `MatterPricingFields` is a thin wrapper over this.
 *
 * Controlled: the parent owns the value and may show validation errors.
 * `errors` keys are i18n keys; pass `showErrors` to surface them (e.g. after a
 * submit attempt).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../TailwindComponents/Dropdown';
import Input from '../TailwindComponents/Input';
import CurrencyPicker from '../TailwindComponents/CurrencyPicker';
import CurrencyInput from '../TailwindComponents/CurrencyInput';
import {
  PricingValue,
  PRICING_MODELS,
  PRICING_MODEL_LABEL_KEYS,
  requiresFixedFee,
  requiresContingency,
  getPricingErrors,
} from './pricingModel';
import type { PricingModel } from '../../../types/economics';

export interface PricingFieldsProps {
  value: PricingValue;
  onChange: (next: PricingValue) => void;
  /** Surface inline validation errors (typically after a submit attempt). */
  showErrors?: boolean;
  /**
   * The entity noun used in the field help text, already translated —
   * "matter", "project". Only the copy changes; the fields do not.
   */
  entityNoun?: string;
  /** Prefix for the input ids, so two forms on one page cannot collide. */
  idPrefix?: string;
}

const PricingFields: React.FC<PricingFieldsProps> = ({
  value,
  onChange,
  showErrors = false,
  entityNoun,
  idPrefix = 'pricing',
}) => {
  const { t } = useTranslation();

  const noun = entityNoun || t('matter');
  const errors = showErrors ? getPricingErrors(value) : {};

  const pricingModelOptions = PRICING_MODELS.map((m) => ({
    value: m,
    label: t(PRICING_MODEL_LABEL_KEYS[m]),
  }));

  const set = (patch: Partial<PricingValue>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <h3 className="text-sm text-gray-700 dark:text-gray-200 border-b border-hairline pb-2">
          {t('Pricing & economics')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('Set how this {{entity}} is billed and in which currency.', { entity: noun })}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pricing model */}
        <div className="flex flex-col space-y-3">
          <Dropdown
            id={`${idPrefix}-pricing-model`}
            label={t('Pricing model')}
            options={pricingModelOptions}
            placeholder={t('Select a pricing model')}
            value={value.pricing_model}
            onChange={(selected) =>
              set({ pricing_model: (Array.isArray(selected) ? selected[0] : selected) as PricingModel })
            }
            className="w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Choose how work on this {{entity}} is billed.', { entity: noun })}
          </p>
        </div>

        {/* Currency */}
        <div className="flex flex-col space-y-3">
          <CurrencyPicker
            id={`${idPrefix}-currency`}
            label={t('Currency')}
            value={value.currency}
            onChange={(currency) => set({ currency })}
            className="w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Currency used for fees, rates and time entries on this {{entity}}.', { entity: noun })}
          </p>
        </div>

        {/* Budget - every pricing model. An hourly engagement is managed
            against a cap just as much as a fixed-fee one, which is why this
            is not gated on the model the way the two fields below are. */}
        <div className="flex flex-col space-y-3">
          <CurrencyInput
            id={`${idPrefix}-budget-amount`}
            label={t('Budget')}
            value={value.budget_amount}
            currency={value.currency}
            onChange={(budget_amount) => set({ budget_amount })}
            error={errors.budget_amount ? t(errors.budget_amount) : undefined}
            className="w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Optional cap for this {{entity}}. Leave empty for no budget.', { entity: noun })}
          </p>
        </div>

        {/* Fixed fee amount - fixed_fee or hybrid */}
        {requiresFixedFee(value.pricing_model) && (
          <div className="flex flex-col space-y-3">
            <CurrencyInput
              id={`${idPrefix}-fixed-fee-amount`}
              label={t('Fixed fee amount')}
              value={value.fixed_fee_amount}
              currency={value.currency}
              onChange={(fixed_fee_amount) => set({ fixed_fee_amount })}
              error={errors.fixed_fee_amount ? t(errors.fixed_fee_amount) : undefined}
              required
              className="w-full"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('The agreed fixed fee for this {{entity}}.', { entity: noun })}
            </p>
          </div>
        )}

        {/* Contingency percentage - contingency or hybrid */}
        {requiresContingency(value.pricing_model) && (
          <div className="flex flex-col space-y-3">
            <Input
              type="number"
              id={`${idPrefix}-contingency-pct`}
              label={t('Contingency %')}
              placeholder="0"
              min={0}
              value={value.contingency_pct}
              onChange={(e) => set({ contingency_pct: e.target.value })}
              error={errors.contingency_pct ? t(errors.contingency_pct) : undefined}
              required
              suffix={<span className="text-gray-500 dark:text-gray-400">%</span>}
              className="w-full"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('Percentage of the outcome charged as the fee (0–100).')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingFields;
